import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Target, Eye, Heart, ShieldCheck, Globe2, Sparkles } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { PageHero, SectionTag, Reveal } from "../components/shared";
import { IMAGES } from "../constants/images";

const VALUE_ICONS = [Heart, ShieldCheck, Globe2, Sparkles];

export default function About() {
  const { t } = useLang();

  return (
    <div data-testid="about-page">
      <PageHero tag={t.about.storyTag} title={t.about.heroTitle} subtitle={t.about.heroSubtitle} image={IMAGES.hero} />

      {/* FOUNDER STORY */}
      <section className="relative py-24" data-testid="founder-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-5 gap-14 items-start">
          <Reveal className="lg:col-span-2">
            <div className="relative lg:sticky lg:top-28">
              <div className="absolute -inset-6 bg-[#1E90FF]/15 blur-[80px] rounded-full" />
              <div className="relative rounded-3xl p-[1.5px] bg-gradient-to-br from-[#1E90FF]/70 via-white/10 to-[#00E5FF]/50">
                <div className="rounded-3xl overflow-hidden bg-[#0F0F12]">
                  <div className="relative aspect-square">
                    <img src={IMAGES.founder} alt="Shrikrishna Keskar" className="w-full h-full object-cover object-center" />
                    <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-white/10" />
                  </div>
                  <div className="flex items-center justify-between px-6 py-5 border-t border-white/8">
                    <div>
                      <div className="font-display font-bold text-white text-lg">{t.about.storyTitle}</div>
                      <div className="font-mono text-[11px] text-[#00E5FF] mt-1 uppercase tracking-wider">{t.about.storyRole}</div>
                    </div>
                    <div className="flex items-center gap-2 font-mono text-[11px] text-zinc-500">
                      <span className="h-2 w-2 rounded-full bg-[#00E5FF] animate-pulse" /> EST. 2016
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-3">
            <SectionTag>{t.about.storyTag}</SectionTag>
            <h2 className="mt-5 text-3xl md:text-4xl font-bold text-white">{t.about.storyTitle}</h2>
            <div className="mt-7 space-y-5">
              {t.about.storyParas.map((p, i) => (
                <p key={i} className="text-zinc-400 text-lg leading-relaxed">{p}</p>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* MISSION & VISION */}
      <section className="relative py-24 bg-[#08080a] border-y border-white/8">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal><SectionTag>{t.about.missionTag}</SectionTag></Reveal>
          <div className="mt-10 grid md:grid-cols-2 gap-6">
            <Reveal>
              <div className="h-full rounded-3xl border border-white/10 bg-[#0F0F12] p-10">
                <Target className="h-9 w-9 text-[#1E90FF]" />
                <h3 className="mt-6 text-2xl font-bold text-white">{t.about.missionTitle}</h3>
                <p className="mt-4 text-zinc-400 text-lg leading-relaxed">{t.about.missionText}</p>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="h-full rounded-3xl border border-white/10 bg-[#0F0F12] p-10">
                <Eye className="h-9 w-9 text-[#00E5FF]" />
                <h3 className="mt-6 text-2xl font-bold text-white">{t.about.visionTitle}</h3>
                <p className="mt-4 text-zinc-400 text-lg leading-relaxed">{t.about.visionText}</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal className="max-w-2xl">
            <SectionTag>{t.about.valuesTag}</SectionTag>
            <h2 className="mt-5 text-3xl md:text-4xl font-bold text-white">{t.about.valuesTitle}</h2>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.about.values.map((v, i) => {
              const Icon = VALUE_ICONS[i];
              return (
                <Reveal key={i} delay={i * 0.07}>
                  <div className="h-full rounded-3xl border border-white/10 bg-[#0F0F12] p-7 transition-all duration-300 hover:border-[#1E90FF]/40 hover:-translate-y-1">
                    <Icon className="h-7 w-7 text-[#00E5FF]" />
                    <h3 className="mt-5 text-lg font-semibold text-white">{v.title}</h3>
                    <p className="mt-2.5 text-sm text-zinc-400 leading-relaxed">{v.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="relative py-24 bg-[#08080a] border-y border-white/8">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal className="max-w-2xl">
            <SectionTag>{t.about.timelineTag}</SectionTag>
            <h2 className="mt-5 text-3xl md:text-4xl font-bold text-white">{t.about.timelineTitle}</h2>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {t.about.timeline.map((m, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="relative rounded-3xl border border-white/10 bg-[#0F0F12] p-8 h-full">
                  <div className="font-mono text-4xl font-bold brand-gradient-text">{m.year}</div>
                  <h3 className="mt-4 text-lg font-semibold text-white">{m.title}</h3>
                  <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{m.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="rounded-[2rem] border border-white/10 bg-gradient-to-br from-[#0d1b2e] to-[#0F0F12] p-12 text-center relative overflow-hidden">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[400px] h-[260px] bg-[#1E90FF]/20 blur-[100px] rounded-full" />
              <h2 className="relative text-2xl md:text-3xl font-bold text-white">{t.cta.title}</h2>
              <Link to="/contact" data-testid="about-cta-contact" className="relative mt-7 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#1E90FF] hover:bg-[#3B82F6] text-white font-semibold transition-all hover:-translate-y-0.5">
                {t.cta.button} <ArrowRight className="h-5 w-5" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
